import React, { useState, useRef } from 'react';
import { Button } from '@mui/material';
import RaList from '../../components/ra-list/RaList';
import RenderControls from '../../components/ra-list/RenderControls';
import { Datagrid, TextField, BooleanField, FunctionField, useDataProvider, Identifier } from 'react-admin';
import { formatLocalTime } from '../../components/ex_dayjs';
import { autoLoad, getRaStore, setRaStore } from '../../utils/common';
import { loadSourceType, loadExchanges, loadDeliveryMethod } from '../../utils/common';
import ApplyFor from './components/apply-for';
import EditRule from './components/edit-rule';
import { getRule } from './rules/rule';
import { getDictionary } from '../aat-primary-accounts/utils';
import { EditRuleDialogState, FieldChoice, RuleAdjustmentRecord, RuleInfo, SelectOption } from './types';
import './style.css';

const DynamicRule = () => {
    const dataProvider = useDataProvider();
    const refbtnFilter = useRef(null);
    const [marketType, setMarketType] = useState<FieldChoice[]>([]);
    const [category, setCategory] = useState<SelectOption[]>([]);
    const [exchanges, setExchange] = useState<SelectOption[]>([]);
    const [aatPAccounts, setAatPAccounts] = useState<string[]>([]);
    const [aatCategoryAcct, setAatCategoryAcct] = useState<(string | number)[]>([]);
    const [delivery, setDelivery] = useState<FieldChoice[]>([]);
    const [sortBy] = useState(['modified desc']);
    const [rcDefault] = useState({ categoryId: 1, skipTag: null });//skipTag = Active
    const [editRuleDialog, setEditRuleDialog] = useState<EditRuleDialogState>({ open: false, record: {}, resource: '', isEdit: undefined });
    const [isLoadingCategory, setIsLoadingCategory] = useState(false);
    const [key, setKey] = useState<Date | string>('');
    const [categoryId, setCategoryId] = useState<number>();
    const [ruleInfo, setRuleInfo] = useState<RuleInfo | null>({});

    React.useEffect(() => {
        setKey(new Date());
        let category = getRaStore('Category');
        if (!category) {
            setIsLoadingCategory(true);
            dataProvider.fetchData('DynamicAdjustRule', 'Category').then((response: any) => {
                if (response.status !== 200 && response.data) {
                    let formatedCategory = response.data?.map((cte: any) => ({ id: cte.id, name: cte.name + ' - ' + cte.description }));
                    setCategory(formatedCategory);
                    setRaStore('Category', formatedCategory);
                    setIsLoadingCategory(false);
                    autoLoad(refbtnFilter);
                }
            }).catch((error: any) => console.log(error));
        }
        else {
            setCategory(category);
            autoLoad(refbtnFilter);
        }
        setCategoryId(rcDefault.categoryId);

        loadSourceType(dataProvider, (response: any) => {
            let formattedMarketType = response?.map((x: any) => ({ name: x.id, label: x.name }));
            setMarketType(formattedMarketType);
        }, false);

        loadExchanges(dataProvider, (response: any) => {
            let convert = response.map((x: any) => ({ id: x.id.toString(), name: x.name }));
            setExchange(convert);
        });

        loadDeliveryMethod(dataProvider, (response: any) => {
            let delivery = response.map((x: any) => ({ name: x.id + '', label: x.name }));
            setDelivery(delivery);
        });

        // load aat primary accounts
        dataProvider
            .fetchData('AAT_PrimaryAccount', 'Filter?$filter=status eq true')
            .then((response: any) => {
                if (response.status !== 200 && response.data) {
                    let arr = [...new Set(response.data.items.filter((x: any) => x.category !== 'Dummy Accts').map((x: any) => x.email))];
                    setAatPAccounts(arr as any[]);
                }
            })
            .catch(console.error);
        // load aat category accounts
        getDictionary('GetAAT_Category', dataProvider, (resp: any) => {
            setAatCategoryAcct(resp.map((x: any) => x.id));
        });
    }, []);

    React.useEffect(() => {
        console.log('categoryId, marketType', categoryId, marketType);
        if (categoryId && marketType.length) {
            let _rule = getRule({ categoryId, marketType, aatPAccounts, aatCategoryAcct, delivery });
            setRuleInfo(_rule);
        }
    }, [categoryId, marketType]);

    React.useEffect(() => {
        console.log('ruleInfo ', ruleInfo);
    }, [ruleInfo]);

    const openEditRule = (id: Identifier, resource: string, record: RuleAdjustmentRecord) => {
        setEditRuleDialog({ ...editRuleDialog, open: true, record: record, resource: resource, isEdit: true });
    };

    const openNewRule = () => {
        setEditRuleDialog({ ...editRuleDialog, open: true, record: {}, resource: '', isEdit: false });
    };

    const closeDialog = () => {
        setEditRuleDialog({ ...editRuleDialog, open: false });
    };

    const handleCategory = (e: any) => {
        setCategoryId(e.value);
        autoLoad(refbtnFilter);
    };

    const controls = [
        { component: 'select', name: 'categoryId', label: 'Category', choices: category, isEmptyOption: false, xs: 2, onChange: handleCategory },
        { component: 'number', name: 'id', label: 'ID' },
        { component: 'text', name: 'name', label: 'Name', xs: 1.5 },
        { component: 'autocomplete', name: 'applyfor_contains', label: 'Apply for', xs: 1.5, choices: exchanges },//apply for which exchange
        { component: 'text', name: 'createdby', label: 'Created By', xs: 1.5 },
        { component: 'text', name: 'modifiedby', label: 'Modified By', xs: 1.5 },
        { component: 'checkbox', name: 'skipTag', label: 'Active', nullable: true }
    ];

    return (
        <>
            {isLoadingCategory && <p>Loading category ...</p>}
            <RaList
                perPage={25}
                title={'Dynamic Adjustment Rule'}
                sortby={sortBy}
                filters={
                    <RenderControls
                        defaultValues={rcDefault}
                        actions={
                            <Button onClick={openNewRule}>+ New Rule</Button>
                        }
                        controls={controls}
                        refFilter={refbtnFilter}
                        exportFile={{
                            filename: `${ruleInfo?.name}-${formatLocalTime(new Date())}`,
                            columns: ['id', 'name', 'condition', 'action', 'applyMarket', 'created', 'createdby', 'modified', 'modifiedby', 'category', 'active'],
                            limit: 1000000
                        }}
                    />
                }
            >
                <Datagrid bulkActionButtons={false} rowClick={openEditRule as any} key={key as any}>
                    <TextField source="id" label={'ID'} />
                    <TextField source="name" label={'Name'} cellClassName='dynamic-rule-name-cell' />
                    <ApplyFor source="applyfor" label={'Apply For'} exchanges={exchanges} />
                    <FunctionField label='Created' source='created' render={record => (record.created ? formatLocalTime(record.created) : '') as string} />
                    <TextField source="createdby" label={'Created By'} />
                    <FunctionField label='Modified' source='modified' render={record => (record.modified ? formatLocalTime(record.modified) : '') as string} />
                    <TextField source="modifiedby" label={'Modified By'} />
                    <BooleanField source="active" label={'Active'} />
                </Datagrid>
            </RaList>
            <EditRule info={editRuleDialog} closeDialog={closeDialog} refbtnFilter={refbtnFilter} exchanges={exchanges} ruleInfo={ruleInfo} dataProvider={dataProvider} />
        </>
    );
};

const DynamicAdjustRulePage = {
    name: 'DynamicAdjustRule',
    list: DynamicRule
};

export default DynamicAdjustRulePage;